import { Component, OnInit } from '@angular/core';
import { EMPTY, expand, finalize, forkJoin, map, Observable, of, reduce, switchMap } from 'rxjs';
import { AlarmPage, AlarmQuery, AlarmStatus, AlarmSummary, DashboardDevice, TelemetrySeries } from '../models/dashboard.model';
import { DashboardService } from '../services/dashboard.service';
import { StorageService } from '../services/storage.service';
import { csvRow, escapeHtml } from '../utils/export';
import { formatCompanyDate, formatCompanyDateTime } from '../utils/company-date';
import { formatObservedDuration, MAX_OBSERVED_GAP_MS, observedDurations, observedRatio, ObservedDurations } from '../utils/report-observations';

type DatePreset = 'today' | '7d' | '30d' | 'custom';
type AlarmStatusFilter = 'all' | AlarmStatus;

@Component({
  selector: 'app-report',
  templateUrl: './report.component.html',
  styleUrl: './report.component.css',
})
export class ReportComponent implements OnInit {
  devices: DashboardDevice[] = [];
  projects: Array<{ id: number; name: string }> = [];
  visibleDevices: DashboardDevice[] = [];
  alarms: AlarmSummary[] = [];
  metricsByDevice = new Map<number, ObservedDurations>();
  loading = false;
  error = false;

  datePreset: DatePreset = '7d';
  status: AlarmStatusFilter = 'all';
  projectId = 0;
  fromDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  toDate = new Date();

  readonly deviceColumns = ['serial', 'worker', 'alarms', 'unhooked', 'sos', 'fall', 'alarmTime', 'activeTime', 'hibernatingTime', 'hookedTime', 'unhookedTime', 'ratios'];
  readonly alarmColumns = ['detectedAt', 'type', 'device', 'worker', 'project', 'severity', 'status'];

  constructor(
    private dashboardService: DashboardService,
    public storageService: StorageService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  private updateDeviceViews(): void {
    const projects = new Map<number, string>();
    this.devices.forEach(device => {
      if (device.project_id && device.project_name) projects.set(Number(device.project_id), device.project_name);
    });
    this.projects = [...projects].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
    this.visibleDevices = this.projectId ? this.devices.filter(device => Number(device.project_id) === this.projectId) : this.devices;
  }

  get summary() {
    return {
      alarms: this.alarms.length,
      devicesWithAlarms: new Set(this.alarms.map(alarm => alarm.deviceId)).size,
      alarmTime: this.totalDuration('alarmMs'),
      unhooked: this.alarmCount('unhooked'),
      sos: this.alarmCount('sos'),
      fall: this.alarmCount('fall'),
    };
  }

  get reportTo(): Date {
    return new Date(this.toDate.getTime() + (this.datePreset === 'custom' ? 24 * 60 * 60 * 1000 : 0));
  }

  get selectedProjectName(): string {
    return this.projects.find(project => project.id === this.projectId)?.name ?? 'All projects';
  }

  setPreset(preset: DatePreset): void {
    this.datePreset = preset;
    const now = new Date();
    this.toDate = now;
    if (preset === 'today') {
      this.fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    } else if (preset === '7d') {
      this.fromDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else if (preset === '30d') {
      this.fromDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    }
    if (preset !== 'custom') this.load();
  }

  load(): void {
    const companyId = Number(this.storageService.companyId);
    const global = this.storageService.roleId === 1 && !companyId;
    if ((!companyId && !global) || this.loading) return;
    this.loading = true;
    this.error = false;
    const alarmQuery: AlarmQuery = {
      ...(this.status !== 'all' ? { status: this.status } : {}),
      ...(this.projectId ? { projectId: this.projectId } : {}),
      from: this.fromDate.toISOString(),
      to: this.reportTo.toISOString(),
      limit: 100,
    };
    forkJoin({
      snapshot: global ? this.dashboardService.getGlobalSnapshot() : this.dashboardService.getSnapshot(companyId),
      alarms: this.getAllAlarms(companyId, global, alarmQuery),
    }).pipe(
      switchMap(result => {
        this.devices = result.snapshot.devices;
        this.updateDeviceViews();
        this.alarms = result.alarms;
        return this.loadMetrics(companyId);
      }),
      finalize(() => this.loading = false),
    ).subscribe({
      next: metrics => {
        this.metricsByDevice = new Map(metrics);
      },
      error: () => this.error = true,
    });
  }

  filtersChanged(): void {
    this.load();
  }

  metric(deviceId: number): ObservedDurations {
    return this.metricsByDevice.get(Number(deviceId)) ?? {
      alarmMs: 0, activeMs: 0, hibernatingMs: 0, stateObservedMs: 0,
      hookedMs: 0, unhookedMs: 0, signalObservedMs: 0,
    };
  }

  alarmCount(type?: string, deviceId?: number): number {
    return this.alarms.filter(alarm =>
      (!type || alarm.alarmType.toLowerCase() === type)
      && (!deviceId || alarm.deviceId === deviceId),
    ).length;
  }

  formatDuration(duration: number): string {
    return formatObservedDuration(duration);
  }

  ratio(part: number, total: number): string {
    return observedRatio(part, total);
  }

  private totalDuration(key: keyof ObservedDurations): number {
    return [...this.metricsByDevice.values()].reduce((total, metrics) => total + metrics[key], 0);
  }

  private getAllAlarms(companyId: number, global: boolean, query: AlarmQuery): Observable<AlarmSummary[]> {
    const page = (cursor?: string | null): Observable<AlarmPage> => global
      ? this.dashboardService.getGlobalAlarms({ ...query, cursor: cursor ?? undefined })
      : this.dashboardService.getAlarms(companyId, { ...query, cursor: cursor ?? undefined });
    return page().pipe(
      expand(result => result.nextCursor ? page(result.nextCursor) : EMPTY),
      reduce((alarms, result) => [...alarms, ...result.alarms], [] as AlarmSummary[]),
    );
  }

  private loadMetrics(companyId: number): Observable<Array<readonly [number, ObservedDurations]>> {
    if (!this.visibleDevices.length) return of([]);
    return forkJoin(this.visibleDevices.map(device => this.loadDeviceMetrics(
      Number(device.company_id) || companyId,
      device.id,
    )));
  }

  private loadDeviceMetrics(companyId: number, deviceId: number): Observable<readonly [number, ObservedDurations]> {
    const from = this.fromDate;
    const to = this.reportTo;
    return forkJoin(this.telemetryWindows(from, to).map(window =>
      this.dashboardService.getDeviceTelemetry(companyId, deviceId, window.from, window.to, 'device_state,state'),
    )).pipe(map(responses => [
      deviceId,
      observedDurations(this.mergeStateSeries(responses.flatMap(response => response.series)), from, to),
    ] as const));
  }

  private telemetryWindows(from: Date, to: Date): Array<{ from: Date; to: Date }> {
    const maxWindowMs = 6 * 24 * 60 * 60 * 1000;
    const windows: Array<{ from: Date; to: Date }> = [];
    for (let start = from.getTime(); start < to.getTime(); start += maxWindowMs) {
      const end = Math.min(start + maxWindowMs, to.getTime());
      windows.push({
        from: new Date(start === from.getTime() ? start - MAX_OBSERVED_GAP_MS : start),
        to: new Date(end),
      });
    }
    return windows;
  }

  private mergeStateSeries(series: TelemetrySeries[]): TelemetrySeries[] {
    return ['device_state', 'state'].map(measure => ({
      measure,
      valueType: 'number' as const,
      encoding: 'bitmask',
      bitmaskSchema: measure,
      points: series.filter(item => item.measure === measure).flatMap(item => item.points),
    }));
  }

  exportCsv(): void {
    const rows = [
      csvRow(['SAFETY REPORT', formatCompanyDate(this.fromDate), formatCompanyDate(this.reportTo)]),
      csvRow(['FILTERS', this.selectedProjectName, this.status]),
      csvRow(['Serial', 'Worker', 'Alarms', 'Unhooked', 'SOS', 'Fall', 'Alarm observed', 'Active observed', 'Hibernating observed', 'Hooked observed', 'Unhooked observed', 'Alarm ratio', 'Hooked ratio']),
      ...this.visibleDevices.map(device => csvRow([
        device.serial_number,
        device.user_name,
        this.alarmCount(undefined, device.id),
        this.alarmCount('unhooked', device.id),
        this.alarmCount('sos', device.id),
        this.alarmCount('fall', device.id),
        this.formatDuration(this.metric(device.id).alarmMs),
        this.formatDuration(this.metric(device.id).activeMs),
        this.formatDuration(this.metric(device.id).hibernatingMs),
        this.formatDuration(this.metric(device.id).hookedMs),
        this.formatDuration(this.metric(device.id).unhookedMs),
        this.ratio(this.metric(device.id).alarmMs, this.metric(device.id).stateObservedMs),
        this.ratio(this.metric(device.id).hookedMs, this.metric(device.id).signalObservedMs),
      ])),
      '',
      csvRow(['ALARMS']),
      csvRow(['Detected', 'Type', 'Device', 'Worker', 'Project', 'Severity', 'Status']),
      ...this.alarms.map(alarm => csvRow([
        formatCompanyDateTime(alarm.detectedAt),
        alarm.alarmType,
        alarm.device.serialNumber,
        alarm.device.userName,
        alarm.project?.name,
        alarm.severity,
        alarm.status,
      ])),
    ];
    const blob = new Blob([`\uFEFF${rows.join('\r\n')}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `jimm-report-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  print(): void {
    const popup = window.open('', '_blank', 'noopener,noreferrer');
    if (!popup) return;
    const deviceRows = this.visibleDevices.map(device => `<tr><td>${escapeHtml(device.serial_number)}</td><td>${escapeHtml(device.user_name || '')}</td><td>${this.alarmCount(undefined, device.id)}</td><td>${escapeHtml(this.formatDuration(this.metric(device.id).alarmMs))}</td><td>${escapeHtml(this.formatDuration(this.metric(device.id).activeMs))}</td><td>${escapeHtml(this.formatDuration(this.metric(device.id).hibernatingMs))}</td><td>${escapeHtml(this.ratio(this.metric(device.id).hookedMs, this.metric(device.id).signalObservedMs))}</td></tr>`).join('');
    const alarmRows = this.alarms.map(alarm => `<tr><td>${escapeHtml(formatCompanyDateTime(alarm.detectedAt))}</td><td>${escapeHtml(alarm.alarmType)}</td><td>${escapeHtml(alarm.device.serialNumber)}</td><td>${escapeHtml(alarm.device.userName || '')}</td><td>${escapeHtml(alarm.status)}</td></tr>`).join('');
    popup.document.write(`<!doctype html><html><head><title>JIMM report</title><style>body{font:12px Arial,sans-serif;color:#17242c;margin:24px}h1{font-size:22px}h2{margin-top:28px;font-size:16px}.summary{display:flex;gap:24px;padding:12px 0;border-block:1px solid #aaa}.summary strong{font-size:20px;display:block}table{width:100%;border-collapse:collapse;margin-top:10px}th,td{padding:7px;border:1px solid #bbb;text-align:left}th{background:#edf1f3}@media print{body{margin:0}}</style></head><body><h1>JIMM safety report</h1><p>${escapeHtml(formatCompanyDate(this.fromDate))} - ${escapeHtml(formatCompanyDate(this.reportTo))}</p><p>Project: ${escapeHtml(this.selectedProjectName)} · Alarm status: ${escapeHtml(this.status)}</p><div class="summary"><span><strong>${this.summary.alarms}</strong>Registered alarms</span><span><strong>${this.summary.devicesWithAlarms}</strong>Devices with alarms</span><span><strong>${escapeHtml(this.formatDuration(this.summary.alarmTime))}</strong>Observed alarm time</span><span><strong>${this.summary.unhooked}</strong>Unhooked</span><span><strong>${this.summary.sos}</strong>SOS</span><span><strong>${this.summary.fall}</strong>Fall</span></div><h2>Device safety observations</h2><table><thead><tr><th>Serial</th><th>Worker</th><th>Alarms</th><th>Alarm observed</th><th>Active observed</th><th>Hibernating observed</th><th>Hooked ratio</th></tr></thead><tbody>${deviceRows}</tbody></table><h2>Alarm history</h2><table><thead><tr><th>Detected</th><th>Type</th><th>Device</th><th>Worker</th><th>Status</th></tr></thead><tbody>${alarmRows}</tbody></table><script>window.onload=()=>window.print()</script></body></html>`);
    popup.document.close();
  }
}
